"use client"

import { useEffect, useState } from "react"
import { X, AtSign, Phone, ChevronDown, ChevronUp } from "lucide-react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { parseExclusionList } from "@/lib/agents/messaging/contact-exclusions"
import { ChannelIcon } from '@/components/ui/channel-icon'
import type { ContactExclusionEntry, MessageAgentConfig } from "@/lib/agents/messaging/types"
import { useTranslations } from "next-intl"


type ExclusionsValue = NonNullable<MessageAgentConfig["contact_exclusions"]>

interface ExclusionListCardProps {
  value: ExclusionsValue
  canManage: boolean
  isSaving: boolean
  /** Receives the COMPLETE object — the config route replaces per-key, partial saves wipe siblings. */
  onSave: (value: Required<ExclusionsValue>) => void
}

/**
 * AI Exclusion List editor. Typed entries (handles + phones) live in a
 * paste-friendly textarea with auto-classification; per-contact entries added
 * from the inbox render as removable chips. The parent owns persistence.
 */
export function ExclusionListCard({ value, canManage, isSaving, onSave }: ExclusionListCardProps) {
  const tUi = useTranslations("Messaging.AgentsMessagingSettingsExclusionListCard")

  // Comma-joined, not one per line: a dozen handles as a column pushes the hint
  // text and the Save button off-screen. Both separators still parse on input.
  const storedTyped = [...value.usernames, ...(value.phones ?? [])].join(", ")
  const storedContacts = value.contacts ?? []

  // Collapsed by default: most agents never touch the list, and it sits between
  // settings that are edited far more often.
  const [expanded, setExpanded] = useState(false)
  const [draft, setDraft] = useState(storedTyped)
  const [contacts, setContacts] = useState<ContactExclusionEntry[]>(storedContacts)
  // Re-sync after a save round-trip; keyed on content, not array identity
  const storedContactsKey = storedContacts
    .map((c) => `${c.channel_type}|${c.external_user_id}`)
    .join(",")
  useEffect(() => setDraft(storedTyped), [storedTyped])
  useEffect(() => setContacts(storedContacts), [storedContactsKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const parsed = parseExclusionList(draft)
  const contactsKey = contacts.map((c) => `${c.channel_type}|${c.external_user_id}`).join(",")
  // Per-kind comparison: a pre-Phase-5 phone stored as a username re-classifies on
  // parse, must render dirty, and one Save migrates it to the phones array.
  const isDirty =
    parsed.usernames.join("\n") !== value.usernames.join("\n") ||
    parsed.phones.join("\n") !== (value.phones ?? []).join("\n") ||
    contactsKey !== storedContactsKey

  const removeContact = (entry: ContactExclusionEntry) =>
    setContacts((prev) =>
      prev.filter(
        (c) =>
          !(c.channel_type === entry.channel_type && c.external_user_id === entry.external_user_id)
      )
    )

  const listedCount = parsed.usernames.length + parsed.phones.length + contacts.length

  return (
    <Card>
      <Collapsible open={expanded} onOpenChange={setExpanded}>
        <CollapsibleTrigger asChild>
          <button type="button" className="w-full text-left">
            <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
              <div className="space-y-1.5">
                <CardTitle className="flex items-center gap-2">
                  <span><span key={tUi("aIExclusionList")}>{tUi("aIExclusionList")}</span></span>
                  {listedCount > 0 && (
                    <Badge variant="secondary" className="font-normal">
                      {listedCount}
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  <span key={tUi("messagesFromTheseContactsNeverGetAnAI")}>{tUi("messagesFromTheseContactsNeverGetAnAI")}</span></CardDescription>
              </div>
              {expanded
                ? <ChevronUp className="w-4 h-4 shrink-0 text-gray-500" />
                : <ChevronDown className="w-4 h-4 shrink-0 text-gray-500" />}
            </CardHeader>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-3">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={"@username, +380671234567, another.handle"}
              rows={3}
              disabled={!canManage}
            />
            <p className="text-xs text-muted-foreground">
              <span key={tUi("oneEntryPerLineOrCommaSeparatedUsernames")}>{tUi("oneEntryPerLineOrCommaSeparatedUsernames")}</span></p>
            <p className="text-xs text-muted-foreground">
              <span><span key={tUi("aDisplayNameNeverMatchesTheInboxShows")}>{tUi("aDisplayNameNeverMatchesTheInboxShows")}</span></span>{" "}
              <span className="font-mono"><span key={"@handle"}>{"@handle"}</span></span> <span><span key={tUi("underTheirNameThatIsTheValueTo")}>{tUi("underTheirNameThatIsTheValueTo")}</span></span>
            </p>
            {(parsed.usernames.length > 0 || parsed.phones.length > 0 || contacts.length > 0) && (
              <div className="flex flex-wrap gap-1">
                {parsed.usernames.map((handle) => (
                  <Badge key={`u-${handle}`} variant="secondary" className="gap-1">
                    <AtSign className="w-3 h-3" />
                    {handle}
                  </Badge>
                ))}
                {parsed.phones.map((phone) => (
                  <Badge key={`p-${phone}`} variant="secondary" className="gap-1">
                    <Phone className="w-3 h-3" />
                    {phone}
                  </Badge>
                ))}
                {/* Same row, not a second list: these are contacts with no handle and no
                    phone, so they cannot be typed above — but they are the same list. */}
                {contacts.map((entry) => (
                  <Badge
                    key={`c-${entry.channel_type}-${entry.external_user_id}`}
                    variant="outline"
                    className="gap-1 pr-1"
                  >
                    <ChannelIcon channelType={entry.channel_type} size={12} />
                    {entry.label ?? entry.external_user_id}
                    {canManage && (
                      <button
                        type="button"
                        onClick={() => removeContact(entry)}
                        title={tUi("removeFromExclusionList")}
                        className="ml-0.5 rounded-sm hover:bg-muted p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </Badge>
                ))}
              </div>
            )}
            <Button
              size="sm"
              onClick={() => onSave({ usernames: parsed.usernames, phones: parsed.phones, contacts })}
              disabled={!canManage || isSaving || !isDirty}
            >
              <span key={tUi("saveList")}>{tUi("saveList")}</span></Button>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  )
}
